/* eslint-disable react-refresh/only-export-components */
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react'
import { OverlayCardPreview, type Overlay } from './overlay-preview'

export type TickerAnimation =
  'SCROLL' | 'FADE' | 'SLIDE' | 'SLIDE_UP' | 'TYPEWRITER'
export type TickerBackgroundMode = 'SOLID' | 'ANIMATED_GRADIENT'
export type TickerRowType =
  'MESSAGE' | 'CLOCK' | 'DATE' | 'PRAYER_TIMES' | 'PRAYER_COUNTDOWN'

export interface PrayerTimes {
  fajr: string
  sunrise?: string | null
  dhuhr: string
  asr: string
  maghrib: string
  isha: string
  jummah?: string | null
}

export interface TickerRow {
  id: string
  type: TickerRowType
  durationMs?: number
  label?: string
  content?: string
  messages?: string[]
  timezone?: string
  locale?: string
  showSeconds?: boolean
  hourCycle?: 'H12' | 'H24'
  periodStyle?: 'UPPER' | 'LOWER' | 'HIDE'
  separator?: string
  prayerTimes?: PrayerTimes
}

export interface Ticker {
  id: string
  name: string
  content: string
  position: 'TOP' | 'BOTTOM'
  direction: 'LEFT' | 'RIGHT'
  animation: TickerAnimation
  rows: TickerRow[]
  rowGap: number
  heightPx: number
  speed: number
  backgroundMode: TickerBackgroundMode
  backgroundColor: string
  gradientColors: string[]
  gradientAngle: number
  gradientSpeed: number
  textColor: string
  fontSize: number
  fontWeight: number
  fontFamily: string
  createdAt: string
  updatedAt: string
}

const defaultPrayerTimes: PrayerTimes = {
  fajr: '05:10',
  sunrise: '06:25',
  dhuhr: '12:15',
  asr: '16:30',
  maghrib: '18:10',
  isha: '19:30',
  jummah: '13:15',
}

export function createTickerRow(type: TickerRowType = 'MESSAGE'): TickerRow {
  return {
    id: crypto.randomUUID(),
    type,
    label: '',
    content: type === 'MESSAGE' ? 'Welcome to DOT IT Signage' : undefined,
    messages: type === 'MESSAGE' ? ['Welcome to DOT IT Signage'] : undefined,
    timezone: 'Asia/Dhaka',
    locale: 'en-GB',
    showSeconds: true,
    hourCycle: 'H12',
    periodStyle: 'LOWER',
    durationMs: 6000,
    separator: '  |  ',
    prayerTimes:
      type === 'PRAYER_TIMES' || type === 'PRAYER_COUNTDOWN'
        ? defaultPrayerTimes
        : undefined,
  }
}

function safeFormatter(
  locale: string,
  options: Intl.DateTimeFormatOptions
): Intl.DateTimeFormat {
  try {
    return new Intl.DateTimeFormat(locale, options)
  } catch {
    return new Intl.DateTimeFormat('en-GB', {
      ...options,
      timeZone: 'UTC',
    })
  }
}

function timeParts(now: Date, timezone: string) {
  const parts = safeFormatter('en-GB', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(now)
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value ?? 0)
  return {
    hour: value('hour'),
    minute: value('minute'),
    second: value('second'),
  }
}

function prayerEntries(times: PrayerTimes) {
  return [
    ['Fajr', times.fajr],
    ['Sunrise', times.sunrise],
    ['Dhuhr', times.dhuhr],
    ['Asr', times.asr],
    ['Maghrib', times.maghrib],
    ['Isha', times.isha],
    ['Jummah', times.jummah],
  ].filter((entry): entry is [string, string] => Boolean(entry[1]))
}

function nextPrayerText(row: TickerRow, now: Date): string {
  const times = row.prayerTimes ?? defaultPrayerTimes
  const timezone = row.timezone || 'Asia/Dhaka'
  const current = timeParts(now, timezone)
  const currentSeconds =
    current.hour * 3600 + current.minute * 60 + current.second
  const daily = prayerEntries(times).filter(([name]) => name !== 'Sunrise')
  let next = daily
    .map(([name, time]) => {
      const [hour, minute] = time.split(':').map(Number)
      return { name, time, seconds: hour * 3600 + minute * 60 }
    })
    .find((entry) => entry.seconds > currentSeconds)
  let remaining: number
  if (!next) {
    const [name, time] = daily[0] ?? ['Fajr', times.fajr]
    const [hour, minute] = time.split(':').map(Number)
    next = { name, time, seconds: hour * 3600 + minute * 60 }
    remaining = 24 * 3600 - currentSeconds + next.seconds
  } else {
    remaining = next.seconds - currentSeconds
  }
  const hours = Math.floor(remaining / 3600)
  const minutes = Math.floor((remaining % 3600) / 60)
  const seconds = remaining % 60
  const countdown = [hours, minutes, seconds]
    .map((value) => String(value).padStart(2, '0'))
    .join(':')
  return `Next prayer: ${next.name} at ${next.time} • ${countdown}`
}

export function tickerRowText(row: TickerRow, now: Date): string {
  const timezone = row.timezone || 'Asia/Dhaka'
  const locale = row.locale || 'en-GB'
  let text: string
  switch (row.type) {
    case 'CLOCK':
      text = safeFormatter(locale, {
        timeZone: timezone,
        hour: '2-digit',
        minute: '2-digit',
        second: row.showSeconds === false ? undefined : '2-digit',
        hourCycle: row.hourCycle === 'H24' ? 'h23' : 'h12',
      })
        .formatToParts(now)
        .filter(
          (part) =>
            part.type !== 'dayPeriod' ||
            (row.hourCycle !== 'H24' && row.periodStyle !== 'HIDE')
        )
        .map((part) => {
          if (part.type !== 'dayPeriod') return part.value
          return row.periodStyle === 'UPPER'
            ? part.value.toLocaleUpperCase(locale)
            : part.value.toLocaleLowerCase(locale)
        })
        .join('')
        .replace(/\s+/g, ' ')
        .trim()
      break
    case 'DATE':
      text = safeFormatter(locale, {
        timeZone: timezone,
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }).format(now)
      break
    case 'PRAYER_TIMES':
      text = prayerEntries(row.prayerTimes ?? defaultPrayerTimes)
        .map(([name, value]) => `${name} ${value}`)
        .join(row.separator || '  |  ')
      break
    case 'PRAYER_COUNTDOWN':
      text = nextPrayerText(row, now)
      break
    case 'MESSAGE':
    default:
      text = row.messages?.length
        ? row.messages.join(row.separator || '  |  ')
        : row.content || ''
  }
  return row.label ? `${row.label}: ${text}` : text
}

function useTickerClock(): Date {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1000)
    return () => window.clearInterval(timer)
  }, [])
  return now
}

function SeamlessTickerTrack({
  text,
  speed,
  direction,
}: {
  text: string
  speed: number
  direction: 'LEFT' | 'RIGHT'
}) {
  const viewportRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLSpanElement>(null)
  const [cycleWidth, setCycleWidth] = useState(0)

  useLayoutEffect(() => {
    const viewport = viewportRef.current
    const measureNode = measureRef.current
    if (!viewport || !measureNode) return
    const measure = () => {
      const nextWidth = Math.ceil(
        Math.max(viewport.clientWidth, measureNode.scrollWidth + 96)
      )
      setCycleWidth((current) => (current === nextWidth ? current : nextWidth))
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(viewport)
    observer.observe(measureNode)
    void document.fonts?.ready.then(measure)
    return () => observer.disconnect()
  }, [text])

  return (
    <div
      ref={viewportRef}
      className='dotit-ticker-viewport relative flex min-w-0 flex-1 items-center'
    >
      <span
        ref={measureRef}
        className='dotit-ticker-measure'
        aria-hidden='true'
      >
        {text}
      </span>
      <div
        className='dotit-ticker-track'
        data-ready={cycleWidth > 0}
        style={
          {
            '--dotit-ticker-cycle-width': `${cycleWidth}px`,
            animationDuration: `${Math.max(4, cycleWidth / speed)}s`,
            animationDirection: direction === 'RIGHT' ? 'reverse' : 'normal',
          } as CSSProperties
        }
      >
        <span>{text}</span>
        <span aria-hidden='true'>{text}</span>
      </div>
    </div>
  )
}

function AnimatedTickerText({
  text,
  animation,
  animationClass,
  durationMs,
  rotating,
}: {
  text: string
  animation: TickerAnimation
  animationClass: string
  durationMs: number
  rotating: boolean
}) {
  const [visibleText, setVisibleText] = useState(text)
  useEffect(() => {
    if (animation !== 'TYPEWRITER') {
      setVisibleText(text)
      return
    }
    if (!text) {
      setVisibleText('')
      return
    }
    let index = 0
    setVisibleText('')
    const characterDelay = Math.min(
      80,
      Math.max(18, Math.floor((durationMs * 0.55) / text.length))
    )
    const intervalId = window.setInterval(() => {
      index += 1
      setVisibleText(text.slice(0, index))
      if (index >= text.length) window.clearInterval(intervalId)
    }, characterDelay)
    return () => {
      window.clearInterval(intervalId)
    }
  }, [animation, durationMs, text])

  const holdClass = !rotating && animationClass ? `${animationClass}-hold` : ''

  return (
    <span
      className={`w-full px-4 text-center ${animationClass} ${holdClass}`}
      style={{
        animationDuration:
          animation === 'TYPEWRITER'
            ? undefined
            : `${rotating ? durationMs : Math.min(1000, durationMs)}ms`,
        animationIterationCount: '1',
        animationFillMode: 'both',
      }}
    >
      {visibleText}
    </span>
  )
}
export function DynamicTickerBar({
  ticker,
  overlay = false,
  className = '',
  leftOverlay,
  rightOverlay,
}: {
  ticker: Ticker
  overlay?: boolean
  className?: string
  leftOverlay?: Overlay | null
  rightOverlay?: Overlay | null
}) {
  const now = useTickerClock()
  const rows = useMemo(
    () =>
      ticker.rows?.length
        ? ticker.rows
        : [
            {
              id: `${ticker.id}-legacy`,
              type: 'MESSAGE' as const,
              content: ticker.content,
              timezone: 'Asia/Dhaka',
              locale: 'en-GB',
              showSeconds: true,
              durationMs: 6000,
            },
          ],
    [ticker]
  )
  const rowIdentity = useMemo(() => rows.map((row) => row.id).join('|'), [rows])
  const [activeRowIndex, setActiveRowIndex] = useState(0)
  const activeRow = rows[activeRowIndex % rows.length] ?? rows[0]!
  const contentText = tickerRowText(activeRow, now).trim() || ticker.content

  useEffect(() => {
    setActiveRowIndex(0)
  }, [ticker.id, rowIdentity])

  useEffect(() => {
    if (rows.length <= 1) return
    const timer = window.setTimeout(
      () => {
        setActiveRowIndex((current) => (current + 1) % rows.length)
      },
      Math.max(1000, Math.min(300000, activeRow.durationMs ?? 6000))
    )
    return () => window.clearTimeout(timer)
  }, [activeRow.durationMs, activeRow.id, rowIdentity, rows.length])
  const activeDurationMs = Math.max(
    1000,
    Math.min(300000, activeRow.durationMs ?? 6000)
  )
  const animationClass =
    ticker.animation === 'FADE'
      ? 'dotit-ticker-fade'
      : ticker.animation === 'SLIDE'
        ? 'dotit-ticker-slide'
        : ticker.animation === 'SLIDE_UP'
          ? 'dotit-ticker-slide-up'
          : ticker.animation === 'TYPEWRITER'
            ? 'dotit-ticker-typewriter'
            : ''

  return (
    <div
      className={`${
        overlay
          ? `absolute right-0 left-0 z-[100] ${
              ticker.position === 'TOP' ? 'top-0' : 'bottom-0'
            }`
          : ''
      } ${ticker.backgroundMode === 'ANIMATED_GRADIENT' ? 'dotit-ticker-animated-gradient' : ''} overflow-hidden ${className}`}
      style={{
        background:
          ticker.backgroundMode === 'ANIMATED_GRADIENT'
            ? `linear-gradient(${ticker.gradientAngle ?? 90}deg, ${(ticker.gradientColors?.length ? ticker.gradientColors : ['#f97316', '#dc2626', '#7c3aed']).join(', ')})`
            : ticker.backgroundColor,
        backgroundSize:
          ticker.backgroundMode === 'ANIMATED_GRADIENT'
            ? '300% 300%'
            : undefined,
        animation:
          ticker.backgroundMode === 'ANIMATED_GRADIENT'
            ? `dotitTickerGradient ${Math.min(30, Math.max(2, ticker.gradientSpeed ?? 8))}s ease-in-out infinite`
            : undefined,
        color: ticker.textColor,
        fontSize: `clamp(0.75rem, ${ticker.fontSize / 18}vw, ${Math.min(ticker.fontSize, 34)}px)`,
        fontWeight: ticker.fontWeight,
        fontFamily: ticker.fontFamily,
      }}
    >
      <div
        className='flex w-full items-stretch'
        data-ticker-height={Math.min(200, Math.max(40, ticker.heightPx ?? 60))}
        style={{
          height: `${Math.min(200, Math.max(40, ticker.heightPx ?? 60))}px`,
        }}
      >
        <div className='flex shrink-0 items-stretch border-r border-white/20 bg-black/15'>
          {leftOverlay ? (
            <OverlayCardPreview overlay={leftOverlay} compact />
          ) : null}
        </div>

        {ticker.animation === 'SCROLL' ? (
          <SeamlessTickerTrack
            key={activeRow.id}
            text={contentText}
            speed={ticker.speed}
            direction={ticker.direction}
          />
        ) : (
          <div className='dotit-ticker-viewport flex min-w-0 flex-1 items-center'>
            <AnimatedTickerText
              key={`${activeRow.id}:${activeRowIndex}`}
              text={contentText}
              animation={ticker.animation}
              animationClass={animationClass}
              durationMs={activeDurationMs}
              rotating={rows.length > 1}
            />
          </div>
        )}

        {rightOverlay ? (
          <div className='flex shrink-0 items-stretch border-l border-white/20 bg-black/15'>
            <OverlayCardPreview overlay={rightOverlay} compact />
          </div>
        ) : null}
      </div>
    </div>
  )
}
